import React, { useMemo, useState } from "react";
import { Check, Plus, Minus, X, Salad, Sparkles, Loader2 } from "lucide-react";

/**
 * IngredientPicker — modal sheet with two configurable columns:
 *   1) Included ingredients: checked by default; unchecking removes them
 *      from the cart line at no cost.
 *   2) Extra ingredients / variants: unchecked by default; each toggle
 *      adds its `price` to the line total.
 * Reads the current qty for this item from `cart` so users can
 * increment/decrement without leaving the sheet. On confirm, calls
 * `onConfirm({ excluded, extras, qty, notes })`.
 */
export default function IngredientPicker({ open, item, cart, onClose, onConfirm }) {
  const line = (cart || {})[item?.id];
  const [qty, setQty] = useState(line?.qty || 1);
  const [excluded, setExcluded] = useState(new Set(line?.excluded_ingredients || []));
  const [extras, setExtras] = useState(new Map(
    (line?.extras || []).map((e) => [e.name, e]),
  ));
  const [notes, setNotes] = useState(line?.notes || "");

  React.useEffect(() => {
    if (!open) return;
    setQty(line?.qty || 1);
    setExcluded(new Set(line?.excluded_ingredients || []));
    setExtras(new Map((line?.extras || []).map((e) => [e.name, e])));
    setNotes(line?.notes || "");
  }, [open, item?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const extrasTotal = useMemo(
    () => Array.from(extras.values()).reduce((s, e) => s + Number(e.price || 0), 0),
    [extras],
  );
  const linePrice = (Number(item?.price || 0) + extrasTotal) * qty;

  if (!open || !item) return null;

  const toggleExcluded = (n) => setExcluded((s) => {
    const next = new Set(s);
    if (next.has(n)) next.delete(n); else next.add(n);
    return next;
  });
  const toggleExtra = (e) => setExtras((m) => {
    const next = new Map(m);
    if (next.has(e.name)) next.delete(e.name); else next.set(e.name, { name: e.name, price: Number(e.price) });
    return next;
  });

  const submit = () => {
    onConfirm({
      qty,
      notes,
      excluded_ingredients: Array.from(excluded),
      extras: Array.from(extras.values()),
    });
  };

  const included = item.included_ingredients || [];
  const availExtras = item.extras || [];
  const hasAny = included.length > 0 || availExtras.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose} data-testid="ingredient-picker">
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-lg max-h-[92vh] overflow-y-auto bg-[#faf3ee] rounded-t-3xl sm:rounded-3xl shadow-xl border border-[#e8dcc8]"
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#4a1c17] text-[#f7ebdf] px-5 py-3 flex items-center justify-between rounded-t-3xl">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#e6c46a]">Personalizza</div>
            <div className="font-serif text-lg font-bold">{item.name}</div>
          </div>
          <button onClick={onClose} className="rounded-full p-1 hover:bg-white/10" data-testid="ingredient-picker-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {!hasAny ? (
          <div className="px-5 py-8 text-center text-sm text-[#7c5a4e]">
            Nessuna personalizzazione disponibile per questo piatto.
          </div>
        ) : (
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Column 1 — Included */}
            {included.length > 0 && (
              <div className="rounded-2xl bg-white border border-[#e8dcc8] p-3">
                <div className="flex items-center gap-1.5 mb-1 text-xs font-semibold text-[#4a1c17] uppercase tracking-wider">
                  <Salad className="h-3.5 w-3.5" /> Inclusi · tocca per modificare
                </div>
                <p className="mb-2 text-[11px] italic text-[#7c5a4e] leading-snug">
                  Non lo vuoi? Toccalo per toglierlo — senza sovrapprezzo.
                </p>
                <ul className="space-y-1.5">
                  {included.map((n) => {
                    const removed = excluded.has(n);
                    return (
                      <li key={n}>
                        <button
                          type="button"
                          onClick={() => toggleExcluded(n)}
                          data-testid={`ing-included-${n}`}
                          className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-left transition-colors ${
                            removed
                              ? "bg-red-50 border border-red-200 text-red-800 line-through"
                              : "bg-[#faf3ee] hover:bg-[#c48b31]/15 text-[#2b1410]"
                          }`}
                        >
                          <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[11px] shrink-0 ${
                            removed ? "bg-red-200 text-red-800" : "bg-[#4a1c17] text-[#f7ebdf]"
                          }`}>
                            {removed ? <X className="h-3 w-3" /> : <Check className="h-3 w-3" />}
                          </span>
                          <span className="flex-1">{n}</span>
                          {/* Discreet minus hint on the right edge, mirroring
                              the "+" hint on the extras column so the two
                              affordances meet visually in the middle. */}
                          {!removed && (
                            <span
                              aria-hidden
                              className="h-5 w-5 rounded-full flex items-center justify-center text-[13px] leading-none shrink-0 bg-[#e8dcc8] text-[#7c5a4e]"
                            >
                              −
                            </span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {excluded.size > 0 && (
                  <p className="mt-2 text-[11px] text-[#7c5a4e]">
                    Tocca per riattivare · nessun sovrapprezzo per gli ingredienti rimossi
                  </p>
                )}
              </div>
            )}

            {/* Column 2 — Extras */}
            {availExtras.length > 0 && (
              <div className="rounded-2xl bg-white border border-[#e8dcc8] p-3">
                <div className="flex items-center gap-1.5 mb-1 text-xs font-semibold text-[#c48b31] uppercase tracking-wider">
                  <Sparkles className="h-3.5 w-3.5" /> Extra & varianti
                </div>
                <p className="mb-2 text-[11px] italic text-[#7c5a4e] leading-snug">
                  Tocca per aggiungerli al tuo piatto.
                </p>
                <ul className="space-y-1.5">
                  {availExtras.map((e) => {
                    const active = extras.has(e.name);
                    return (
                      <li key={e.name}>
                        <button
                          type="button"
                          onClick={() => toggleExtra(e)}
                          data-testid={`ing-extra-${e.name}`}
                          className={`w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-left transition-colors ${
                            active
                              ? "bg-[#c48b31]/20 border border-[#c48b31] text-[#4a1c17]"
                              : "bg-[#faf3ee] hover:bg-[#c48b31]/15 text-[#2b1410]"
                          }`}
                        >
                          <span className={`h-5 w-5 rounded-full flex items-center justify-center text-[11px] shrink-0 ${
                            active ? "bg-[#c48b31] text-[#2b1410]" : "bg-[#e8dcc8] text-[#7c5a4e]"
                          }`}>
                            {active ? <Check className="h-3 w-3" /> : "+"}
                          </span>
                          <span className="flex-1">{e.name}</span>
                          <span className={`text-xs font-semibold ${active ? "text-[#4a1c17]" : "text-[#7c5a4e]"}`}>
                            +€ {Number(e.price).toFixed(2)}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Notes + qty + submit */}
        <div className="p-4 space-y-3 border-t border-[#e8dcc8] bg-white/60">
          <div>
            <label className="block text-[11px] font-semibold text-[#7c5a4e] uppercase tracking-wider mb-1">Note aggiuntive</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="es. senza glutine, poco piccante, allergie…"
              data-testid="ingredient-picker-notes"
              className="w-full rounded-xl border border-[#e8dcc8] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4a1c17]/30"
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-1">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} data-testid="ingredient-picker-minus"
                className="h-9 w-9 rounded-full bg-[#faf3ee] border border-[#e8dcc8] text-[#4a1c17]"><Minus className="h-4 w-4 mx-auto" /></button>
              <span className="w-8 text-center font-semibold text-[#2b1410]" data-testid="ingredient-picker-qty">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(20, q + 1))} data-testid="ingredient-picker-plus"
                className="h-9 w-9 rounded-full bg-[#4a1c17] text-[#f7ebdf]"><Plus className="h-4 w-4 mx-auto" /></button>
            </div>
            <button
              onClick={submit}
              data-testid="ingredient-picker-submit"
              className="flex-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-2.5 text-sm font-semibold"
            >
              Aggiungi · € {linePrice.toFixed(2)}
            </button>
          </div>
          {extrasTotal > 0 && (
            <p className="text-[11px] text-center text-[#c48b31]">
              +€ {extrasTotal.toFixed(2)} extra × {qty} = € {(extrasTotal * qty).toFixed(2)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
